import React from "react";
import {
  ArrowDown, ArrowUp, Check, ChevronLeft, ChevronRight, Columns3,
  Database, RefreshCw, Search, SlidersHorizontal, X,
} from "lucide-react";

import { getMarketRank } from "../../api/client";
import { number, signed, toneClass } from "./marketFormat";
import styles from "./rankPage.module.css";

const STORAGE_KEY = "rank-visible-columns-v1";
const OPTIONAL_COLUMNS = [
  { key: "revenueYoy", label: "营收同比 %" },
  { key: "profitYoy", label: "净利同比 %" },
  { key: "profitQoq", label: "利润环比 %" },
  { key: "dividend", label: "股息率 %" },
  { key: "industryGroup", label: "行业组" },
];
const DEFAULT_FILTERS = {
  query: "", exchange: "all", board: "all", industry_id: "",
  market_cap_min: "", market_cap_max: "", pe_ttm_min: "", pe_ttm_max: "",
  pb_min: "", pb_max: "", dv_ttm_min: "", revenue_yoy_min: "",
  profit_yoy_min: "", profit_qoq_min: "", listing_age_max_days: "",
};
const QUICK_FILTERS = [
  ["大市值", { market_cap_min: "500" }],
  ["低市盈率", { pe_ttm_min: "0", pe_ttm_max: "15" }],
  ["低市净率", { pb_min: "0", pb_max: "1.5" }],
  ["高股息", { dv_ttm_min: "3" }],
  ["营收增长", { revenue_yoy_min: "20" }],
  ["净利增长", { profit_yoy_min: "20" }],
];

function readVisibleColumns() {
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    return Array.isArray(value) ? value.filter((key) => OPTIONAL_COLUMNS.some((column) => column.key === key)) : [];
  } catch {
    return [];
  }
}

function rankFactMeta(payload) {
  const factDate = payload.fact_dates?.price ?? payload.page_date ?? "--";
  const latestQuoteTime = (payload.items ?? [])
    .map((item) => item.quote_time)
    .filter(Boolean)
    .sort((left, right) => right.localeCompare(left))[0];
  if (payload.quote_mode === "local_complete_intraday" && latestQuoteTime) {
    return {
      factDate,
      note: `当前页报价至 ${latestQuoteTime.replace("T", " ").slice(0, 16)}`,
    };
  }
  return { factDate, note: `${factDate} 收盘行情` };
}


function Field({ label, children }) {
  return <label className={styles.field}><span>{label}</span>{children}</label>;
}

function RangeInput({ value, onChange, placeholder }) {
  return <input value={value} onChange={(event) => onChange(event.target.value)} inputMode="decimal" placeholder={placeholder} />;
}

function FilterPanel({ filters, industries, onChange, onApply, onPreset, onClear, busy }) {
  return (
    <section className={styles.filterPanel} aria-label="选股条件">
      <div className={styles.presetRow}>
        <strong>快捷条件</strong>
        {QUICK_FILTERS.map(([label, values]) => (
          <button
            key={label}
            type="button"
            aria-pressed={Object.entries(values).every(([key, value]) => filters[key] === value)}
            onClick={() => onPreset(values)}
          >
            {label}
          </button>
        ))}
        <button className={styles.clearPreset} type="button" onClick={onClear}>清空</button>
      </div>
      <form onSubmit={(event) => { event.preventDefault(); onApply(); }}>
        <div className={styles.filterRow}>
          <span className={styles.filterGroup}>基础</span>
          <Field label="市场">
            <select value={filters.exchange} onChange={(event) => onChange("exchange", event.target.value)}>
              <option value="all">全部</option><option value="SZ">深交所</option><option value="SH">上交所</option><option value="BJ">北交所</option>
            </select>
          </Field>
          <Field label="板块">
            <select value={filters.board} onChange={(event) => onChange("board", event.target.value)}>
              <option value="all">全部</option><option value="MAIN">主板</option><option value="STAR">科创板</option><option value="CHINEXT">创业板</option><option value="BSE">北交所</option>
            </select>
          </Field>
          <Field label="市值">
            <select value={filters.market_cap_min} onChange={(event) => onChange("market_cap_min", event.target.value)}>
              <option value="">不限</option><option value="50">≥50亿</option><option value="100">≥100亿</option><option value="500">≥500亿</option><option value="1000">≥1000亿</option>
            </select>
          </Field>
          <Field label="行业">
            <select className={styles.industrySelect} value={filters.industry_id} onChange={(event) => onChange("industry_id", event.target.value)}>
              <option value="">全部行业</option>
              <optgroup label="申万一级">
                {industries.filter((item) => item.level === "L1").map((item) => <option key={item.industry_id} value={item.industry_id}>{item.name}</option>)}
              </optgroup>
              <optgroup label="申万二级">
                {industries.filter((item) => item.level === "L2").map((item) => <option key={item.industry_id} value={item.industry_id}>{item.name}</option>)}
              </optgroup>
            </select>
          </Field>
          <label className={styles.searchField}><Search size={16} /><input value={filters.query} onChange={(event) => onChange("query", event.target.value)} placeholder="股票名称或代码" /></label>
        </div>
        <div className={styles.filterRow}>
          <span className={styles.filterGroup}>估值</span>
          <Field label="市盈率"><RangeInput value={filters.pe_ttm_max} onChange={(value) => onChange("pe_ttm_max", value)} placeholder="上限" /></Field>
          <Field label="市净率"><RangeInput value={filters.pb_max} onChange={(value) => onChange("pb_max", value)} placeholder="上限" /></Field>
          <Field label="股息率"><RangeInput value={filters.dv_ttm_min} onChange={(value) => onChange("dv_ttm_min", value)} placeholder="最低 %" /></Field>
        </div>
        <div className={styles.filterRow}>
          <span className={styles.filterGroup}>财报增长</span>
          <Field label="营收同比"><RangeInput value={filters.revenue_yoy_min} onChange={(value) => onChange("revenue_yoy_min", value)} placeholder="最低 %" /></Field>
          <Field label="净利同比"><RangeInput value={filters.profit_yoy_min} onChange={(value) => onChange("profit_yoy_min", value)} placeholder="最低 %" /></Field>
          <Field label="利润环比"><RangeInput value={filters.profit_qoq_min} onChange={(value) => onChange("profit_qoq_min", value)} placeholder="最低 %" /></Field>
          <Field label="上市时间">
            <select value={filters.listing_age_max_days} onChange={(event) => onChange("listing_age_max_days", event.target.value)}>
              <option value="">不限</option><option value="30">近30日新股</option><option value="90">近90日新股</option><option value="365">近1年新股</option>
            </select>
          </Field>
          <button className={styles.applyButton} type="submit" disabled={busy}>
            {busy ? <RefreshCw className={styles.spin} size={16} /> : <SlidersHorizontal size={16} />}筛选
          </button>
        </div>
      </form>
    </section>
  );
}

function ColumnDialog({ open, selected, onToggle, onClose, triggerRef }) {
  const dialogRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const dialog = dialogRef.current;
    dialog?.querySelector("input")?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") { onClose(); return; }
      if (event.key !== "Tab" || !dialog) return;
      const focusable = [...dialog.querySelectorAll('button, input:not([disabled])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      (triggerRef.current || previous)?.focus?.();
    };
  }, [open, onClose, triggerRef]);
  if (!open) return null;
  return (
    <div className={styles.modalOverlay} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section ref={dialogRef} className={styles.columnDialog} role="dialog" aria-modal="true" aria-labelledby="rank-column-dialog-title">
        <button className={styles.dialogCloseIcon} type="button" aria-label="关闭显示列配置" onClick={onClose}><X size={20} /></button>
        <h2 id="rank-column-dialog-title">显示列配置</h2>
        <p>勾选下方基础事实字段，可在表格中额外显示。</p>
        <div className={styles.columnChecks}>
          {OPTIONAL_COLUMNS.map((column) => (
            <label key={column.key}>
              <input type="checkbox" checked={selected.includes(column.key)} onChange={() => onToggle(column.key)} />
              <span className={styles.checkBox} aria-hidden="true"><Check size={13} /></span>
              {column.label}
            </label>
          ))}
        </div>
        <div className={styles.dialogActions}><button type="button" onClick={onClose}>关闭</button></div>
      </section>
    </div>
  );
}

function SortHeader({ field, label, sort, order, onSort }) {
  const active = sort === field;
  return <button type="button" className={active ? styles.sortActive : ""} onClick={() => onSort(field)}>{label}{active ? (order === "desc" ? <ArrowDown size={14} /> : <ArrowUp size={14} />) : null}</button>;
}

function SortColumnHeader({ field, label, sort, order, onSort }) {
  const direction = sort === field ? (order === "desc" ? "descending" : "ascending") : "none";
  return (
    <th aria-sort={direction}>
      <SortHeader field={field} label={label} sort={sort} order={order} onSort={onSort} />
    </th>
  );
}

function RankTable({ payload, visibleColumns, sort, order, onSort, onOpen }) {
  return (
    <div className={styles.tableScroller}>
      <table>
        <thead><tr>
          <th>#</th><th>股票</th><th>行业</th>
          <SortColumnHeader field="close" label="现价" sort={sort} order={order} onSort={onSort} />
          <SortColumnHeader field="pct_chg" label="今日涨跌" sort={sort} order={order} onSort={onSort} />
          <SortColumnHeader field="pct_chg_5d" label="5日涨跌" sort={sort} order={order} onSort={onSort} />
          <SortColumnHeader field="total_mv_yi" label="总市值(亿)" sort={sort} order={order} onSort={onSort} />
          <SortColumnHeader field="pe_ttm" label="PE TTM" sort={sort} order={order} onSort={onSort} />
          <SortColumnHeader field="pb" label="PB" sort={sort} order={order} onSort={onSort} />
          {visibleColumns.includes("revenueYoy") ? <SortColumnHeader field="revenue_yoy" label="营收同比" sort={sort} order={order} onSort={onSort} /> : null}
          {visibleColumns.includes("profitYoy") ? <SortColumnHeader field="profit_yoy" label="净利同比" sort={sort} order={order} onSort={onSort} /> : null}
          {visibleColumns.includes("profitQoq") ? <SortColumnHeader field="profit_qoq" label="利润环比" sort={sort} order={order} onSort={onSort} /> : null}
          {visibleColumns.includes("dividend") ? <SortColumnHeader field="dv_ttm" label="股息率" sort={sort} order={order} onSort={onSort} /> : null}
          {visibleColumns.includes("industryGroup") ? <th>行业组</th> : null}
        </tr></thead>
        <tbody>
          {payload.items.map((item, index) => (
            <tr key={item.instrument_id}>
              <td className={styles.rankNumber}>{(payload.page - 1) * payload.page_size + index + 1}</td>
              <td><button type="button" className={styles.stockLink} onClick={() => onOpen(item.code)}><strong>{item.name}</strong><span>{item.code}</span></button></td>
              <td><div className={styles.industryCell}><strong>{item.l1_industry || "--"}</strong><span>{item.l2_industry || "--"}</span></div></td>
              <td>{number(item.close)}</td>
              <td className={toneClass(item.pct_chg, styles)}>{signed(item.pct_chg)}</td>
              <td className={toneClass(item.pct_chg_5d, styles)}>{signed(item.pct_chg_5d)}</td>
              <td>{number(item.total_mv_yi, 1)}</td><td>{number(item.pe_ttm)}</td><td>{number(item.pb)}</td>
              {visibleColumns.includes("revenueYoy") ? <td className={toneClass(item.revenue_yoy, styles)}>{signed(item.revenue_yoy)}</td> : null}
              {visibleColumns.includes("profitYoy") ? <td className={toneClass(item.profit_yoy, styles)}>{signed(item.profit_yoy)}</td> : null}
              {visibleColumns.includes("profitQoq") ? <td className={toneClass(item.profit_qoq, styles)}>{signed(item.profit_qoq)}</td> : null}
              {visibleColumns.includes("dividend") ? <td>{signed(item.dv_ttm)}</td> : null}
              {visibleColumns.includes("industryGroup") ? <td>{item.l2_industry || "--"}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RankPage({ snapshotId, onStatus, navigate }) {
  const [filters, setFilters] = React.useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = React.useState(DEFAULT_FILTERS);
  const [payload, setPayload] = React.useState(null);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(true);
  const [sort, setSort] = React.useState("pct_chg");
  const [order, setOrder] = React.useState("desc");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [visibleColumns, setVisibleColumns] = React.useState(readVisibleColumns);
  const triggerRef = React.useRef(null);
  const requestSequence = React.useRef(0);

  const load = React.useCallback(async () => {
    const requestId = ++requestSequence.current;
    setBusy(true); setError("");
    try {
      const response = await getMarketRank({ snapshotId, ...appliedFilters, sort, order, page, page_size: pageSize });
      if (requestId !== requestSequence.current) return;
      setPayload(response.data);
      onStatus?.({ status: "ok", dataDate: response.meta.business_date });
    } catch (caught) {
      if (requestId !== requestSequence.current) return;
      setError(caught.message || "选股数据暂时无法读取");
      onStatus?.({ status: "unavailable", dataDate: null });
    } finally {
      if (requestId === requestSequence.current) setBusy(false);
    }
  }, [appliedFilters, onStatus, order, page, pageSize, snapshotId, sort]);
  React.useEffect(() => { void load(); }, [load]);

  const apply = React.useCallback((next = filters) => { setPage(1); setAppliedFilters({ ...next }); }, [filters]);
  const preset = (values) => { const next = { ...DEFAULT_FILTERS, ...values }; setFilters(next); apply(next); };
  const clear = () => { setFilters(DEFAULT_FILTERS); apply(DEFAULT_FILTERS); };
  const changeSort = (field) => { setPage(1); if (field === sort) setOrder((value) => value === "desc" ? "asc" : "desc"); else { setSort(field); setOrder("desc"); } };
  const toggleColumn = (key) => setVisibleColumns((current) => {
    const next = current.includes(key) ? current.filter((item) => item !== key) : [...current, key];
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
  });
  const factMeta = payload ? rankFactMeta(payload) : null;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeading}>
        <div><span className={styles.eyebrow}>STOCK SCREENER</span><h1>基础选股</h1><p>按行情、估值、股息与已披露财务事实筛选，不包含私有评分。</p></div>
        {payload ? (
          <div className={styles.headingMeta}>
            <div><span>快照日期</span><strong>{payload.page_date}</strong></div>
            <div><span>价格事实</span><strong>{factMeta.factDate}</strong></div>
            <small>{factMeta.note}</small>
          </div>
        ) : null}
      </header>

      <FilterPanel filters={filters} industries={payload?.industry_options ?? []} onChange={(key, value) => setFilters((current) => ({ ...current, [key]: value }))} onApply={apply} onPreset={preset} onClear={clear} busy={busy} />

      <section className={styles.resultsPanel} aria-busy={busy}>
        <div className={styles.resultsHeader}>
          <div><h2>筛选结果：<em>{payload?.total?.toLocaleString("zh-CN") ?? "--"}</em> 只</h2><span>/ 共 {payload?.market_total?.toLocaleString("zh-CN") ?? "--"} 只</span></div>
          <div className={styles.resultTools}>
            {busy ? <span className={styles.loadingLabel}><RefreshCw className={styles.spin} size={15} />正在筛选</span> : null}
            <button ref={triggerRef} className={styles.columnButton} type="button" aria-label="显示列配置" onClick={() => setDialogOpen(true)}><Columns3 size={18} /></button>
          </div>
        </div>
        {error ? <div className={styles.statePanel} role="alert"><Database size={22} /><strong>选股数据暂时无法读取</strong><span>{error}</span><button onClick={load}>重新读取</button></div> : null}
        {!error && payload && payload.items.length ? <RankTable payload={payload} visibleColumns={visibleColumns} sort={sort} order={order} onSort={changeSort} onOpen={(code) => navigate(`/stock/${code}?from=${encodeURIComponent("/rank")}`)} /> : null}
        {!error && payload && !payload.items.length ? <div className={styles.statePanel}><Search size={22} /><strong>没有符合当前条件的股票</strong><span>请放宽筛选条件后重试。</span></div> : null}
        {!payload && !error ? <div className={styles.statePanel}><RefreshCw className={styles.spin} size={22} /><strong>正在读取本地选股事实</strong></div> : null}
        {payload ? <footer className={styles.pagination}>
          <label>每页<select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}><option value="10">10</option><option value="20">20</option><option value="50">50</option></select>条</label>
          <span>第 {payload.page} / {payload.page_count} 页</span>
          <button type="button" aria-label="上一页" disabled={page <= 1 || busy} onClick={() => setPage((value) => value - 1)}><ChevronLeft size={17} /></button>
          <button type="button" aria-label="下一页" disabled={page >= payload.page_count || busy} onClick={() => setPage((value) => value + 1)}><ChevronRight size={17} /></button>
        </footer> : null}
      </section>
      <ColumnDialog open={dialogOpen} selected={visibleColumns} onToggle={toggleColumn} onClose={() => setDialogOpen(false)} triggerRef={triggerRef} />
    </div>
  );
}
